import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { Conversation } from '../../hooks/useChat';
import CustomToast from '../../components/Redesing/custom-toast';
import { AssignAdminDialog, adminLabel } from './AssignAdminDialog';
import { PanelSection } from './PanelSection';
import { TagPicker } from './TagPicker';
import { ChatTagGlossary, HardwareFamily } from './tagGlossary';
import { transferNoteText } from './transferNote';

interface Props {
  conversation: Conversation;
  /** The signed-in admin's ant id; null until ant answers (or when it cannot). */
  myAdminId: string | null;
  /** CS's glossary; null while it loads or when it could not be read. */
  glossary: ChatTagGlossary | null;
  glossaryError?: string | null;
  /** The session's rental family, for the picker's filter; undefined while unknown. */
  hardwareFamily: HardwareFamily | null | undefined;
  onAssign: (convId: string, adminId: string | null) => Promise<void>;
  onSetPriority: (convId: string, priority: 'normal' | 'urgent') => Promise<void>;
  /** One glossary tag on; rejects with ant's refusal ("not in the glossary or retired"). */
  onAddTag: (convId: string, tag: string) => Promise<void>;
  /** One tag off, whatever it is (a retired tag, an old string). */
  onRemoveTag: (convId: string, tag: string) => Promise<void>;
  /** Posts an internal note on the session (the transfer note). */
  onAddNote?: (convId: string, note: string) => Promise<void>;
  /** An assistant session: shown, not changed, until an agent takes it over. */
  readOnly?: boolean;
  /**
   * Another agent has the chat: no Assign to me, since Take over (in the
   * composer) is how it becomes this agent's, and no Unassign, which would
   * leave it for this agent's next reply to claim without a word. Reassign,
   * through its dialog, stays.
   */
  heldByOther?: boolean;
}

type Busy = null | 'assign' | 'priority';

const errorText = (error: unknown) =>
  error instanceof Error ? error.message : String(error ?? '');

/**
 * Assignment, priority and tags of the session, each written to ant and
 * reflected from the session ant returns. Assignment and priority stop once
 * the session is closed (ant opens a new session for the rider's next
 * message); tags do not: CS tags at wrap-up and adds markers after seeing
 * the next session. Only the assistant's sessions are read-only throughout.
 */
export function ConversationControls({
  conversation,
  myAdminId,
  glossary,
  glossaryError = null,
  hardwareFamily,
  onAssign,
  onSetPriority,
  onAddTag,
  onRemoveTag,
  onAddNote,
  readOnly = false,
  heldByOther = false,
}: Props) {
  const [busy, setBusy] = useState<Busy>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Read-only (closed, or the assistant's) is shown like a closed session.
  const closed = conversation.status === 'closed' || readOnly;
  const assignedToMe = Boolean(myAdminId) && conversation.assigned_admin_id === myAdminId;
  const isUrgent = conversation.priority === 'urgent';

  const run = async (
    kind: Exclude<Busy, null>,
    work: () => Promise<void>,
    success: string,
    failure: string
  ): Promise<boolean> => {
    setBusy(kind);
    try {
      await work();
      CustomToast({ type: 'success', message: success });
      return true;
    } catch (error) {
      CustomToast({
        type: 'error',
        message: `${failure} ${errorText(error)}`.trim(),
        duration: 6000,
      });
      return false;
    } finally {
      setBusy(null);
    }
  };

  const assign = (adminId: string | null, name: string | null) =>
    run(
      'assign',
      () => onAssign(conversation.id, adminId),
      adminId ? `Assigned to ${name}` : 'Unassigned',
      adminId
        ? 'Could not assign the conversation.'
        : 'Could not unassign the conversation.'
    );

  const togglePriority = () =>
    run(
      'priority',
      () => onSetPriority(conversation.id, isUrgent ? 'normal' : 'urgent'),
      isUrgent ? 'Priority set to normal' : 'Marked as urgent',
      'Could not change the priority.'
    );

  /** A tag write: the picker shows the chip pending; a refusal is said here and the chip drops. */
  const tagWrite = async (work: () => Promise<void>, failure: string) => {
    try {
      await work();
    } catch (error) {
      CustomToast({
        type: 'error',
        message: `${failure} ${errorText(error)}`.trim(),
        duration: 6000,
      });
    }
  };

  return (
    <>
      <PanelSection title="Assignee">
        <Typography variant="body2" sx={{ mb: 1 }} data-testid="assignee">
          {conversation.assigned_admin_name
            ? assignedToMe
              ? `${conversation.assigned_admin_name} (you)`
              : conversation.assigned_admin_name
            : 'Unassigned'}
        </Typography>
        {!closed && (
          <Stack direction="row" gap={1} flexWrap="wrap">
            {!assignedToMe && !heldByOther && myAdminId && (
              <Button
                size="small"
                variant="contained"
                disabled={busy !== null}
                onClick={() => assign(myAdminId, 'you')}
                startIcon={
                  busy === 'assign' ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : undefined
                }
              >
                Assign to me
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              disabled={busy !== null}
              onClick={() => setPickerOpen(true)}
              startIcon={<PersonAddAlt1Icon fontSize="small" />}
            >
              {conversation.assigned_admin_id ? 'Reassign' : 'Assign'}
            </Button>
            {conversation.assigned_admin_id && !heldByOther && (
              <Button
                size="small"
                color="inherit"
                disabled={busy !== null}
                onClick={() => assign(null, null)}
              >
                Unassign
              </Button>
            )}
          </Stack>
        )}
      </PanelSection>

      <PanelSection title="Priority">
        <FormControlLabel
          control={
            <Switch
              size="small"
              color="error"
              checked={isUrgent}
              disabled={closed || busy !== null}
              onChange={togglePriority}
              inputProps={{ 'aria-label': 'Urgent' }}
            />
          }
          label={
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="body2">Urgent</Typography>
              {busy === 'priority' && <CircularProgress size={12} />}
            </Stack>
          }
        />
      </PanelSection>

      <PanelSection title="Tags">
        <TagPicker
          glossary={glossary}
          glossaryError={glossaryError}
          selected={conversation.tags ?? []}
          family={hardwareFamily}
          disabled={readOnly}
          onAdd={(tag) =>
            tagWrite(() => onAddTag(conversation.id, tag), 'Could not add the tag.')
          }
          onRemove={(tag) =>
            tagWrite(() => onRemoveTag(conversation.id, tag), 'Could not remove the tag.')
          }
        />
      </PanelSection>

      <AssignAdminDialog
        open={pickerOpen}
        currentAdminId={conversation.assigned_admin_id}
        busy={busy === 'assign'}
        onClose={() => setPickerOpen(false)}
        onAssign={async (admin, note) => {
          const name = adminLabel(admin);
          if (!(await assign(admin.id, name))) return;
          setPickerOpen(false);
          if (note && onAddNote) {
            try {
              await onAddNote(conversation.id, transferNoteText(name, note));
            } catch (error) {
              CustomToast({
                type: 'error',
                message: `Assigned, but the note could not be added. ${errorText(error)}`,
                duration: 6000,
              });
            }
          }
        }}
      />
    </>
  );
}
